import {
  ArrowLeftOutlined,
  LeftOutlined,
  RightOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  describeError,
  dirhams,
  isNegative,
  type MonthKey,
  monthLabel,
  operationsCount,
  present,
  TYPE_LABELS,
} from '@horof/shared';
import { Alert, Button, DatePicker, Skeleton } from 'antd';
import dayjs from 'dayjs';
import { type ReactNode, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useQuery } from 'urql';
import { ShareBar } from './charts';
import { financeContext } from './data';
import { monthRange, nextMonth, parseMonth, previousMonth } from './dates';
import { share } from './summary';
import { MonthDocument } from './summary-data';
import { PageHeader } from './ui';

/** Operations of the month, with more filters. */
function opsLink(month: MonthKey, extra: Record<string, string> = {}) {
  const [from, to] = monthRange(month);
  return `/operations?${new URLSearchParams({ from, to, ...extra })}`;
}

type Total = {
  key: string | null;
  label: string;
  income: string;
  expenses: string;
  result?: string;
  count?: number;
};

/**
 * «ملخص الشهر»: arqam's totals for the month (doc 55: transfers never count,
 * loan money apart) by category, project and account, the month before, the
 * work left, the transfers, loan money and in-kind contributions.
 */
export function MonthScreen() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const month = parseMonth(params.get('m'));
  const [from, to] = monthRange(month);
  const [prevFrom, prevTo] = monthRange(previousMonth(month));
  const [{ data, error }] = useQuery({
    query: MonthDocument,
    variables: { from, to, prevFrom, prevTo },
    context: useMemo(() => financeContext, []),
  });
  const setMonth = (m: MonthKey) => setParams({ m }, { replace: true });

  const total = data?.total?.nodes[0];
  const before = data?.previous?.nodes[0];
  const categories = present(data?.byCategory?.nodes);
  const transfers = present(data?.accountTransfers?.nodes);
  const loans = present(data?.loans?.nodes);
  const inKind = present(data?.inKindContributions?.nodes);

  return (
    <section className="desk-page desk-summary">
      <PageHeader
        title="ملخص الشهر"
        actions={
          <>
            <span className="desk-month-picker">
              <Button
                icon={<RightOutlined />}
                title="الشهر السابق"
                aria-label="الشهر السابق"
                onClick={() => setMonth(previousMonth(month))}
              />
              <DatePicker
                picker="month"
                allowClear={false}
                format="MMMM YYYY"
                value={dayjs(`${month}-01`)}
                onChange={(d) => d && setMonth(d.format('YYYY-MM') as MonthKey)}
              />
              <Button
                icon={<LeftOutlined />}
                title="الشهر التالي"
                aria-label="الشهر التالي"
                onClick={() => setMonth(nextMonth(month))}
              />
            </span>
            <Button
              icon={<UnorderedListOutlined />}
              onClick={() => navigate(opsLink(month))}
            >
              عمليات الشهر
            </Button>
          </>
        }
      />

      {error && (
        <Alert
          type="error"
          showIcon
          message={describeError(error, 'تعذّر تحميل ملخص الشهر.')}
        />
      )}
      {!data && !error && <Skeleton active paragraph={{ rows: 10 }} />}

      {data && total && (
        <div className="desk-summary-body">
          <div className="desk-kpis">
            <Kpi
              label="المداخيل"
              value={total.income}
              before={before?.income}
              tone="credit"
            />
            <Kpi
              label="المصاريف"
              value={total.expenses}
              before={before?.expenses}
              tone="debit"
            />
            <Kpi
              label="النتيجة"
              value={total.result}
              before={before?.result}
              tone={isNegative(total.result) ? 'debit' : 'credit'}
            />
            <div className="desk-kpi">
              <span className="field-hint">العمليات</span>
              <strong className="ltr-num">{total.count}</strong>
              <span className="field-hint">
                {operationsCount(data.all?.totalCount ?? 0)} مع التحويلات
                والقروض
              </span>
            </div>
          </div>
          <div className="field-hint">
            بدون التحويلات بين الحسابات وأموال القروض، كما في التقرير السنوي.
            المقارنة مع {monthLabel(previousMonth(month))}.
          </div>

          <div className="desk-summary-grid">
            <Card title="المداخيل حسب الصنف">
              <Breakdown
                rows={categories.filter((c) => c.income !== '0.00')}
                amount={(c) => c.income}
                total={total.income}
                tone="credit"
                link={(k) =>
                  opsLink(
                    month,
                    k
                      ? { category: k, type: 'credit' }
                      : { missing: 'category', type: 'credit' },
                  )
                }
              />
            </Card>
            <Card title="المصاريف حسب الصنف">
              <Breakdown
                rows={categories.filter((c) => c.expenses !== '0.00')}
                amount={(c) => c.expenses}
                total={total.expenses}
                tone="debit"
                link={(k) =>
                  opsLink(
                    month,
                    k
                      ? { category: k, type: 'debit' }
                      : { missing: 'category', type: 'debit' },
                  )
                }
              />
            </Card>
            <Card title="حسب المشروع">
              <TotalsTable
                rows={present(data.byProject?.nodes)}
                head="المشروع"
                link={(k) => opsLink(month, { project: k })}
              />
            </Card>
            <Card title="حسب الحساب">
              <TotalsTable
                rows={present(data.byAccount?.nodes)}
                head="الحساب"
                link={(k) => opsLink(month, { account: k })}
              />
              <div className="field-hint">بدون التحويلات بين الحسابات.</div>
            </Card>
            <Card title="ما يجب إكماله في هذا الشهر">
              <WorkLine
                label="تحتاج مراجعة"
                n={data.review?.totalCount ?? 0}
                to={opsLink(month, { review: '1' })}
              />
              <WorkLine
                label="بلا صنف"
                n={data.noCategory?.totalCount ?? 0}
                to={opsLink(month, { missing: 'category' })}
              />
              <WorkLine
                label="بلا جهة"
                n={data.noCounterparty?.totalCount ?? 0}
                to={opsLink(month, { missing: 'counterparty' })}
              />
              <WorkLine
                label="مصاريف بلا وصل"
                n={data.noReceipt?.totalCount ?? 0}
                to={opsLink(month, { missing: 'receipt' })}
              />
            </Card>
            <Card title="التحويلات وأموال القروض">
              {transfers.length === 0 && loans.length === 0 && (
                <div className="field-hint">لا تحويلات ولا قروض هذا الشهر.</div>
              )}
              {transfers.map((t) => (
                <Link
                  key={t.id}
                  to={`/transfers?panel=${t.id}`}
                  className="desk-line"
                >
                  <span className="ltr-num">{t.date}</span>
                  <span>
                    {t.fromAccount?.name} <ArrowLeftOutlined />{' '}
                    {t.toAccount?.name}
                  </span>
                  <strong>{dirhams(t.amount)}</strong>
                </Link>
              ))}
              {loans.map((o) => (
                <Link
                  key={o.id}
                  to={`/debts?panel=${o.debtId}`}
                  className="desk-line"
                >
                  <span className="ltr-num">{o.date}</span>
                  <span>قرض: {o.description || TYPE_LABELS[o.type]}</span>
                  <span className={`desk-amount is-${o.type}`}>
                    {o.type === 'credit' ? '+' : '−'} {dirhams(o.amount)}
                  </span>
                </Link>
              ))}
            </Card>
            {inKind.length > 0 && (
              <Card title="مساهمات عينية (لا تدخل في الحسابات النقدية)">
                {inKind.map((k) => (
                  <Link
                    key={k.id}
                    to={`/in-kind?panel=${k.id}`}
                    className="desk-line"
                  >
                    <span className="ltr-num">{k.date}</span>
                    <span>
                      {k.description} · {k.counterparty?.name ?? 'متبرع مجهول'}
                    </span>
                    <strong>{dirhams(k.estimatedValue)}</strong>
                  </Link>
                ))}
              </Card>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="desk-card">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function Kpi({
  label,
  value,
  before,
  tone,
}: {
  label: string;
  value: string;
  before?: string;
  tone: 'credit' | 'debit';
}) {
  return (
    <div className="desk-kpi">
      <span className="field-hint">{label}</span>
      <strong className={`desk-amount is-${tone}`}>{dirhams(value)}</strong>
      {before !== undefined && (
        <span className="field-hint">الشهر السابق: {dirhams(before)}</span>
      )}
    </div>
  );
}

function Breakdown({
  rows,
  amount,
  total,
  tone,
  link,
}: {
  rows: Total[];
  amount: (row: Total) => string;
  total: string;
  tone: 'credit' | 'debit';
  link: (key: string | null) => string;
}) {
  if (!rows.length) return <div className="field-hint">لا شيء.</div>;
  return (
    <div className="desk-breakdown">
      {rows.map((r) => (
        <Link
          key={r.key ?? 'none'}
          to={link(r.key)}
          className="desk-breakdown-row"
        >
          {/* No count: arqam's counts both types of a category together. */}
          <span className={r.key ? '' : 'is-missing'}>{r.label}</span>
          <ShareBar percent={share(amount(r), total)} tone={tone} />
          <strong>{dirhams(amount(r))}</strong>
        </Link>
      ))}
    </div>
  );
}

function TotalsTable({
  rows,
  head,
  link,
}: {
  rows: Total[];
  head: string;
  link: (key: string) => string;
}) {
  if (!rows.length) return <div className="field-hint">لا شيء.</div>;
  return (
    <table className="desk-mini-table">
      <thead>
        <tr>
          <th>{head}</th>
          <th>المداخيل</th>
          <th>المصاريف</th>
          <th>النتيجة</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key ?? 'none'}>
            <td>{r.key ? <Link to={link(r.key)}>{r.label}</Link> : r.label}</td>
            <td className="is-credit">{dirhams(r.income)}</td>
            <td className="is-debit">{dirhams(r.expenses)}</td>
            <td className={isNegative(r.result) ? 'is-debit' : ''}>
              {dirhams(r.result)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function WorkLine({ label, n, to }: { label: string; n: number; to: string }) {
  return (
    <Link to={to} className={`desk-line${n ? '' : ' is-done'}`}>
      <span>{label}</span>
      <strong className="ltr-num">{n}</strong>
    </Link>
  );
}
